import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { Icon } from './Icon';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: string;
}

export interface SegmentedButtonProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Show the check mark on the selected segment (design default). */
  check?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  'aria-label'?: string;
}

/** Design pill segmented control: selected segment in primary container with a check mark. */
export function SegmentedButton<T extends string>({
  options,
  value,
  onChange,
  check = true,
  size = 'medium',
  fullWidth,
  ...aria
}: SegmentedButtonProps<T>) {
  const h = size === 'small' ? 36 : 40;
  return (
    <Box
      role="radiogroup"
      aria-label={aria['aria-label']}
      sx={{
        display: fullWidth ? 'flex' : 'inline-flex',
        border: 1,
        borderColor: 'outline',
        borderRadius: `${h / 2}px`,
        overflow: 'hidden',
        height: h,
      }}
    >
      {options.map((o, i) => {
        const selected = o.value === value;
        return (
          <ButtonBase
            key={o.value}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(o.value)}
            sx={(t) => ({
              flex: fullWidth ? 1 : undefined,
              px: 2,
              gap: '8px',
              fontSize: 13,
              fontWeight: 600,
              borderInlineStart: i > 0 ? `1px solid ${t.vars.palette.outline}` : undefined,
              bgcolor: selected ? t.vars.palette.primary.container : 'transparent',
              color: selected ? t.vars.palette.primary.onContainer : t.vars.palette.text.secondary,
            })}
          >
            {check && selected && <Icon name="check" size={16} />}
            {o.icon && !(check && selected) && <Icon name={o.icon} size={18} />}
            {o.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
